import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './IndicatorActionLogTable.css';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

export default function IndicatorActionLogTable() {
  const { workspace } = useWorkspace();
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('ALL');
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('ALL');
  const [selectedCandleCount, setSelectedCandleCount] = useState('');
  const candleCountOptions = ['5', '10', '20', '50', '100'];

  const actionOptions = useMemo(() => {
    if (!Array.isArray(logs)) return [];
    const set = new Set();
    logs.forEach((log) => log.action && set.add(log.action));
    return Array.from(set);
  }, [logs]);

  const processStatusOptions = useMemo(() => {
    if (!Array.isArray(logs)) return [];
    const set = new Set();
    logs.forEach((log) => log.processStatus && set.add(log.processStatus));
    return Array.from(set);
  }, [logs]);

  const candleCloseTimeOptions = useMemo(() => {
    if (!Array.isArray(logs)) return [];
    const set = new Set();
    logs.forEach((log) => log.calculatedCandleCloseTime && set.add(log.calculatedCandleCloseTime));
    return Array.from(set).sort();
  }, [logs]);

  const headers = {
    seq: '#',
    symbol: 'Symbol',
    timeFrame: 'Time Frame',
    calculatedCandleCloseTime: 'Candle Close Time',
    action: 'Action',
    processStatus: 'Process Status',
    strictType: 'Strict Type',
    executionNotes: 'Execution Notes',
    createdDate: 'Created Date',
    modifiedDate: 'Modified Date'
  };

  const columns = Object.keys(headers);

  useEffect(() => {
    if (selectedTimeFrame === 'ALL') {
      setSelectedCandleCount('');
    }
  }, [selectedTimeFrame]);

  useEffect(() => {
    fetch(buildScopedApiUrl('/api/bot/ui/get/getRecentActiveAlertSymbols', workspace), {
      headers: getApiHeaders()
    })
      .then((res) => res.json())
      .then((data) => {
        const safeSymbols = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn("Unexpected indicator action symbol payload shape", workspace, data);
        }
        setSymbolOptions(['ALL', ...Array.from(new Set(safeSymbols))]);
      })
      .catch(console.error);
  }, [workspace]);

  const fetchLogsBySelection = useCallback(() => {
    let url = '';
    const tf = selectedTimeFrame;
    const candles = selectedCandleCount;

    if (selectedSymbol === 'ALL' && tf !== 'ALL' && candles !== '') {
      url = buildScopedApiUrl('/api/bot/ui/get/getIndicatorActionLogByTimeFrameAndCandleCloseTimes', workspace, {
        timeFrame: tf,
        noOfLatestCloseTimes: candles,
      });
    } else if (selectedSymbol === 'ALL' && tf === 'ALL') {
      url = buildScopedApiUrl('/api/bot/ui/get/getAllIndicatorActionLogByCutOff', workspace);
    } else if (tf === 'ALL') {
      url = buildScopedApiUrl('/api/bot/ui/get/getAllIndicatorActionLogBySymbol', workspace, {
        symbol: selectedSymbol,
      });
    } else if (selectedSymbol === 'ALL') {
      url = buildScopedApiUrl('/api/bot/ui/get/getAllIndicatorActionLogByTimeFrame', workspace, {
        timeFrame: tf,
      });
    } else {
      url = buildScopedApiUrl('/api/bot/ui/get/getAllIndicatorActionLogBySymbolAndTimeFrame', workspace, {
        symbol: selectedSymbol,
        timeFrame: tf,
      });
    }

    fetch(url, { headers: getApiHeaders() })
      .then((res) => res.json())
      .then((data) => {
        const safeLogs = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn("Invalid logs response:", data);
        }
        setLogs(safeLogs);
      })
      .catch(console.error);
  }, [selectedSymbol, selectedTimeFrame, selectedCandleCount, workspace]);

  useEffect(() => {
    fetchLogsBySelection();
  }, [fetchLogsBySelection]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredLogs = ensureArray(logs).filter((log) =>
    columns.every((key) => {
      const filterVal = filters[key];
      const fieldVal = log[key];
      if (!filterVal) return true;

      if (key === 'timeFrame') {
        const numVal = parseFloat(fieldVal);
        const match = filterVal.match(/^([<>]=?|=)?\s*(-?\d+(\.\d+)?)$/);
        if (match) {
          const op = match[1] || '=';
          const filterNum = parseFloat(match[2]);
          if (Number.isNaN(numVal)) return false;
          switch (op) {
            case '>': return numVal > filterNum;
            case '>=': return numVal >= filterNum;
            case '<': return numVal < filterNum;
            case '<=': return numVal <= filterNum;
            case '=':
            default:
              return numVal === filterNum;
          }
        }
        return fieldVal?.toString() === filterVal.trim();
      }

      return fieldVal?.toString().toLowerCase().includes(filterVal.toLowerCase());
    })
  );

  const sortedLogs = [...filteredLogs].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;

    let aVal = a[key];
    let bVal = b[key];

    if (key === 'timeFrame') {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal);
      if (Number.isNaN(aVal)) return 1;
      if (Number.isNaN(bVal)) return -1;
    }

    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    return 0;
  });

  const formatDate = (timestamp) =>
    timestamp ? new Date(timestamp).toLocaleString() : '-';

  return (
    <div className="trade-log-table-wrapper">
      <h4>Indicator Action Logs</h4>
      <p style={{ marginTop: 0, color: '#666' }}>
        Workspace: <strong>{workspace.exchange}</strong> / <strong>{workspace.marketType}</strong>
      </p>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <label>
          Symbol:&nbsp;
          <select value={selectedSymbol} onChange={(e) => setSelectedSymbol(e.target.value)}>
            {symbolOptions.map((sym) => (
              <option key={sym} value={sym}>{sym}</option>
            ))}
          </select>
        </label>

        <label>
          Time Frame:&nbsp;
          <select value={selectedTimeFrame} onChange={(e) => setSelectedTimeFrame(e.target.value)}>
            {['ALL', '5', '15', '30', '60', '240', '480', '720', '1440'].map((tf) => (
              <option key={tf} value={tf}>{tf}</option>
            ))}
          </select>
        </label>

        <label>
          Recent Candle Count:&nbsp;
          <input
            type="text"
            list="indicatorActionCandleCountSuggestions"
            value={selectedCandleCount}
            onChange={(e) => setSelectedCandleCount(e.target.value)}
            placeholder="e.g. 10"
            disabled={selectedTimeFrame === 'ALL'}
          />
          <datalist id="indicatorActionCandleCountSuggestions">
            {candleCountOptions.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        </label>

        <button onClick={fetchLogsBySelection}>Fetch Logs</button>
        <button onClick={() => setFilters({})}>Clear All Filters</button>
      </div>

      <table className="trade-log-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col} onClick={() => handleSort(col)}>
                {headers[col]}
                {sortConfig.key === col ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr>
          <tr>
            {columns.map((col) => {
              const val = filters[col] || '';
              if (col === 'symbol') {
                return (
                  <th key={col}>
                    <select value={val} onChange={(e) => handleFilterChange(col, e.target.value)}>
                      <option value="">All</option>
                      {symbolOptions.filter((option) => option !== 'ALL').map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </th>
                );
              }
              if (col === 'timeFrame') {
                return (
                  <th key={col}>
                    <input
                      type="text"
                      placeholder="e.g. 15 or >30"
                      value={val}
                      onChange={(e) => handleFilterChange(col, e.target.value)}
                    />
                  </th>
                );
              }
              if (col === 'action') {
                return (
                  <th key={col}>
                    <select value={val} onChange={(e) => handleFilterChange(col, e.target.value)}>
                      <option value="">All</option>
                      {actionOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </th>
                );
              }
              if (col === 'processStatus') {
                return (
                  <th key={col}>
                    <select value={val} onChange={(e) => handleFilterChange(col, e.target.value)}>
                      <option value="">All</option>
                      {processStatusOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </th>
                );
              }
              if (col === 'calculatedCandleCloseTime') {
                return (
                  <th key={col}>
                    <select value={val} onChange={(e) => handleFilterChange(col, e.target.value)}>
                      <option value="">All</option>
                      {candleCloseTimeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </th>
                );
              }

              return (
                <th key={col}>
                  <input
                    type="text"
                    placeholder={`Filter ${headers[col]}`}
                    value={val}
                    onChange={(e) => handleFilterChange(col, e.target.value)}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedLogs.map((log, idx) => (
            <tr key={`${log.symbol}|${log.calculatedCandleCloseTime}|${idx}`}>
              {columns.map((col) => {
                const isSeq = col === 'seq';
                const value = isSeq ? idx + 1 : log[col];
                const isStatus = col === 'processStatus';
                const cellStyle = isStatus
                  ? value === 'ERROR'
                    ? { backgroundColor: '#fee2e2' }
                    : value === 'PROCESSED'
                      ? { backgroundColor: '#dcfce7' }
                      : {}
                  : {};

                return (
                  <td key={col} style={cellStyle}>
                    {col.toLowerCase().includes('date') || col.includes('Timestamp')
                      ? formatDate(value)
                      : value || '-'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
